import { __spreadArray, __read } from 'tslib';
import React__default, { useRef, useCallback } from 'react';
import throttle from 'lodash/throttle';
import { ANIMATION_CLASSNAME_SELECTOR, getElementList, callWithVisible, addClassnameForVisible, animationAlreadyInUse } from './classManipulation.esm.js';
import classes from '../../styles/animations/transitions.module.esm.js';

var THROTTLE_DELAY = 100;
/**
 *
 * useAddAnimationClassnames should be called at top level component that is using nucleobase,
 * for marcom that would be Page. for camp-editor PagePreview
 * use the name (ANIMATION_CLASSNAME_BASE + '-' + TAnimation) in the className of the element
 *
 * Example:
 * ANIMATION_CLASSNAME_BASE = 'animation'
 * TAnimation = 'fade-in | fade-up'
 *
 * <div class="example-class animation-fade-up"></div>
 * <div className="example-class animation-fade-in"></div>
 *
 * !!IMPORTANT: cannot be used for elements that already use `transform` in their styles
 */
function useAddAnimationClassnames() {
    //list of elements and accompanying classes. Also saves information if the element was animated
    var listOfElements = useRef([]);
    // 1) attaches the initial-position(start) class
    // 2) handles animation for already visible elements
    var handleInitialClassname = function (list) {
        //attach initial-position(start) class for all elements
        //attach finishing class if the element is visible in viewport
        return callWithVisible(list, function (element, isVisible) {
            //attach the initial class
            element.node.classList.add(element.animationClass.start);
            //check that  the element is visible
            if (isVisible)
                addClassnameForVisible(element, element.animationClass.end);
        });
    };
    //initiate the animation for an element added by ref
    var initAnimationForElement = function (_a) {
        var ref = _a.ref;
        //exit if no reference or the elements classname already has attached animation classes
        if (!ref || animationAlreadyInUse(ref.className))
            return;
        var list = getElementList([ref], classes);
        // attach the initial-position(start) class and handle animation for already visible elements
        list = handleInitialClassname(list);
        listOfElements.current = __spreadArray(__spreadArray([], __read(listOfElements.current), false), __read(list), false);
    };
    var initAnimation = useCallback(function () {
        //get the list of elements that have a classname that starts with `animation`
        var elements = document.querySelectorAll(ANIMATION_CLASSNAME_SELECTOR);
        // get list of elements and accompanying classes
        var list = getElementList(elements, classes);
        // attach the initial-position(start) class and handle animation for already visible elements
        list = handleInitialClassname(list);
        // save the list for further usage
        listOfElements.current = list;
    }, []);
    React__default.useEffect(function () {
        initAnimation();
    }, [initAnimation]);
    //listen to the scroll and if the element is visible, attach the animation class
    var listenToScroll = useCallback(function () {
        if (listOfElements.current.length === 0)
            return;
        //attach the visible(end) animation
        listOfElements.current = callWithVisible(listOfElements.current, function (element, isVisible) {
            //check that  the element is visible
            if (isVisible)
                element.node.classList.add(element.animationClass.end);
        });
    }, []);
    //connect the scroll listener that will attach the visible(end) animation when the element will be visible.
    React__default.useEffect(function () {
        //listen to window scroll
        var throttledListenToScroll = throttle(listenToScroll, THROTTLE_DELAY);
        window.addEventListener('scroll', throttledListenToScroll);
        //clear listener on component unmount
        return function () { return window.removeEventListener('scroll', throttledListenToScroll); };
    }, [listenToScroll]);
    return {
        initAnimationForElement: initAnimationForElement,
    };
}

export { useAddAnimationClassnames as default };
//# sourceMappingURL=useAddAnimationClassnames.esm.js.map
